class ProductRecommendations extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()

    if (window?.onTagalysReady && this.recommendationId && this.productId) {
      if (this.dataset.bab === 'true') {
        // eslint-disable-next-line no-undef
        onTagalysReady(() => this.fetchTagalysBoughtAlsoBought())
      } else {
        // eslint-disable-next-line no-undef
        onTagalysReady(() => this.fetchTagalysRecommendations())
      }
    } else {
      this.observeForShopifyRecommendations()
    }
  }

  observeForShopifyRecommendations () {
    const observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return
        observer.unobserve(this)
        this.fetchShopifyRecommendations()
      },
      {
        rootMargin: '0px 0px 500px 0px',
      },
    )

    observer.observe(this)
  }

  async fetchShopifyRecommendations () {
    if (!this.productId) return

    try {
      const response = await fetch(
        `${this.baseUrl}?section_id=${this.sectionId}&product_id=${this.productId}&limit=${this.limit}&intent=related`,
      )
      const text = await response.text()
      const html = new DOMParser().parseFromString(text, 'text/html')
      const recommendations = html.querySelector('product-recommendations')

      if (recommendations && recommendations.innerHTML.trim()) {
        this.innerHTML = recommendations.innerHTML
      } else {
        this.dispatchEmptyEvent()
      }
    } catch (error) {
      this.handleError(error)
    }
  }

  dispatchEmptyEvent () {
    this.dispatchEvent(
      new CustomEvent('recommendations:empty', {
        bubbles: true,
        detail: { index: this.dataset.index },
      }),
    )
  }

  async fetchTagalysBoughtAlsoBought () {
    try {
      const response = await this.getTagalysBoughtAlsoBought()
      if (response?.products?.length > 0) {
        const cards = await this.createProductCards(response.products)
        this.renderCarousel(cards, response.products.length)
      } else {
        this.fetchShopifyRecommendations()
      }
    } catch (error) {
      this.dispatchEmptyEvent()
      this.handleError(error)
    }
  }

  getTagalysBoughtAlsoBought () {
    return new Promise((resolve, reject) => {
      window.Tagalys.APIConnector.BoughtAlsoBought.call({
        params: {
          productId: this.productId,
          limit: this.limit,
        },
        onSuccess: (response) => resolve(response),
        onFailure: (error) => reject(error),
      })
    })
  }

  async fetchTagalysRecommendations () {
    try {
      const response = await this.getTagalysRecommendations()
      if (response?.products?.length > 0) {
        const cards = await this.createProductCards(response.products)

        if (this.placement === 'view-similar') {
          this.innerHTML = `<div class="drw-ViewSimilarRecommendations_Grid">${cards}</div>`
        } else {
          this.renderCarousel(cards, response.products.length)
        }
      } else {
        this.fetchShopifyRecommendations()
      }
    } catch (error) {
      this.dispatchEmptyEvent()
      this.handleError(error)
    }
  }

  getTagalysRecommendations () {
    const scopeFilter = {
      _variant_size: [this.sizeName], // Replace with the desired variant size
    }

    return new Promise((resolve, reject) => {
      window.Tagalys.APIConnector.Recommendations.call({
        params: {
          recommendationId: this.recommendationId,
          productId: this.productId,
          limit: this.limit,
        },
        beforeAPICall (params) {
          return {
            ...params,
            scope: scopeFilter,
          }
        },
        onSuccess: (response) => resolve(response),
        onFailure: (error) => reject(error),
      })
    })
  }

  async createProductCards (products) {
    const cards = await Promise.all(
      products.map(async (product) => {
        const productUrl = `${window.Shopify.routes.root}products/${product.handle}`

        let sectionToRender
        if (
          this.placement === 'product-carousel' ||
          this.placement === 'view-similar'
        ) {
          sectionToRender = 'product-card'
        } else {
          sectionToRender = 'push-cart-product-card'
        }

        try {
          const res = await window.BAO.sectionRenderer.fetchSingle(
            sectionToRender,
            productUrl,
          )
          const newDOM = window.BAO.sectionRenderer.convertToDOM(res)
          const article = newDOM.querySelector('article')

          if (!article) return

          if (this.placement === 'view-similar') {
            return article.outerHTML
          }

          if (this.placement === 'product-carousel') {
            return `
              <div class="sec-ProductCarousel_Slide keen-slider__slide"
                  data-product-carousel-el="slide"
                  data-tagalys-slide>
                ${article.outerHTML}
              </div>
            `
          }

          if (this.placement === 'push-cart') {
            return `
              <div class="psh-Recommendations_Slide keen-slider__slide"
                  data-push-cart-carousel-el="slide"
                  data-tagalys-slide>
                ${article.outerHTML}
              </div>
            `
          }

          if (this.placement === 'back-in-stock') {
            return `
              <div class="bis-Recommendations_Slide keen-slider__slide"
                  data-back-in-stock-carousel-el="slide"
                  data-tagalys-slide>
                ${article.outerHTML}
              </div>
            `
          }

          // Fallback
          return article.outerHTML
        } catch (error) {
          this.handleError(error)
          return ''
        }
      }),
    )

    return cards.join('')
  }

  renderCarousel (cards, length) {
    if (this.placement === 'product-carousel') {
      this.innerHTML = `
        <product-carousel class="sec-ProductCarousel_CarouselElement">
          <div class="sec-ProductCarousel_Controls">
            <button type="button" class="sec-ProductCarousel_Control" data-product-carousel-el="previous" disabled="" aria-hidden="false"><svg width="12" height="12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fill-rule="evenodd" clip-rule="evenodd" d="m7.823 10.354.707-.708L4.884 6 8.53 2.354l-.707-.708L3.469 6l4.354 4.354Z" fill="currentColor"></path></svg>
            </button>

            <button type="button" class="sec-ProductCarousel_Control" data-product-carousel-el="next" aria-hidden="false"><svg width="12" height="12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fill-rule="evenodd" clip-rule="evenodd" d="m4.177 1.646-.708.708L7.116 6 3.469 9.646l.708.707L8.53 6 4.177 1.646Z" fill="currentColor"></path></svg>
          </button>
        </div>

          <div class="sec-ProductCarousel_Carousel">
            <div class="sec-ProductCarousel_Slides keen-slider" data-product-carousel-el="slides">
              ${cards}
            </div>
          </div>
        </product-carousel>
      `
    } else if (this.placement === 'back-in-stock') {
      this.innerHTML = `
        <back-in-stock-carousel class="bis-Recommendations_Section">
          <div class="bis-Recommendations_Header">
            <p class="bis-Recommendations_Title fz-12_120">${this.dataset.title}</p>
            ${
              length > 1
                ? `
              <div class="bis-Recommendations_Controls">
                <button type="button" class="bis-Recommendations_Control" data-back-in-stock-carousel-el="previous">
                  <svg width="12" height="12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="m7.823 10.354.707-.708L4.884 6 8.53 2.354l-.707-.708L3.469 6l4.354 4.354Z" fill="currentColor" />
                  </svg>
                </button>
                <button type="button" class="bis-Recommendations_Control" data-back-in-stock-carousel-el="next">
                  <svg width="12" height="12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="m4.177 1.646-.708.708L7.116 6 3.469 9.646l.708.707L8.53 6 4.177 1.646Z" fill="currentColor" />
                  </svg>
                </button>
              </div>
              `
                : ''
            }
          </div>
          <div class="bis-Recommendations_Carousel">
            <div class="bis-Recommendations_Slides keen-slider" data-back-in-stock-carousel-el="slides">
              ${cards}
            </div>
          </div>
        </back-in-stock-carousel>
      `
    } else {
      this.innerHTML = `
        <push-cart-carousel class="psh-Recommendations_Section">
          <div class="psh-Recommendations_Header">
            <p class="psh-Recommendations_Title fz-12_120">${this.dataset.title}</p>
            ${
              length > 1
                ? `
              <div class="psh-Recommendations_Controls">
                <button type="button" class="psh-Recommendations_Control" data-push-cart-carousel-el="previous">
                  <svg width="12" height="12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="m7.823 10.354.707-.708L4.884 6 8.53 2.354l-.707-.708L3.469 6l4.354 4.354Z" fill="currentColor" />
                  </svg>
                </button>
                <button type="button" class="psh-Recommendations_Control" data-push-cart-carousel-el="next">
                  <svg width="12" height="12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="m4.177 1.646-.708.708L7.116 6 3.469 9.646l.708.707L8.53 6 4.177 1.646Z" fill="currentColor" />
                  </svg>
                </button>
              </div>
              `
                : ''
            }
          </div>
          <div class="psh-Recommendations_Carousel">
            <div class="psh-Recommendations_Slides keen-slider" data-push-cart-carousel-el="slides">
              ${cards}
            </div>
          </div>
        </push-cart-carousel>
      `
    }
  }

  handleError (error) {
    console.error('Error:', error)
    this.fetchShopifyRecommendations() // Fallback to Shopify recommendations
  }

  get baseUrl () {
    return this.dataset.baseUrl
  }

  get limit () {
    return this.dataset.limit || 8
  }

  get productId () {
    return this.dataset.productId || ''
  }

  get sectionId () {
    return this.dataset.sectionId || ''
  }

  get recommendationId () {
    return this.dataset.recommendationId || ''
  }

  get placement () {
    return this.dataset.placement || 'product-carousel'
  }

  get sizeName () {
    return this.dataset.size || ''
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations)
}
